import { Alert, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { formatDate } from '../lib/format';

export const EMISSION_POINTS_PATH = '/admin/v1/fiscal/emission-points';

/**
 * A number range, with the server's own verdict attached.
 *
 * The counting used to happen here — `rangeEnd - nextNumber + 1` in a table cell, and a day count
 * off the browser's clock — while a nightly job on the server counted the same thing a different
 * way and got a different answer. Two places, two answers, one range. The arithmetic now happens
 * once, on the server, and this reads it.
 *
 * What stays here is the ENGLISH. A severity and a reason code are not something to put in front
 * of the owner; the sentences below are, and they are the only place they exist.
 */
export interface EmissionPoint {
  id: string;
  emissionPointCode: string;
  /** What this block of numbers prints. A credit note range is not a register's range. */
  documentType: 'invoice' | 'creditNote' | 'debitNote';
  /** Which register prints from it, if any. A range nobody has attached is not a register either. */
  deviceId: string | null;
  nextNumber: number;
  rangeEnd: number;
  deadline: string;
  active: boolean;
  /** Server-computed. See the fiscal domain's thresholds module. */
  remaining: number;
  daysUntilDeadline: number | null;
  severity: 'ok' | 'warning' | 'blocked';
  reasons: ('lowNumbers' | 'closingDeadline' | 'exhausted' | 'pastDeadline' | 'inactive')[];
  canIssue: boolean;
  correlativoNext: string;
}

function countdown(days: number): string {
  if (days === 0) return 'that is today';
  if (days === 1) return 'that is tomorrow';
  if (days < 0) return 'that has already passed';
  return `that is in ${days} days`;
}

export interface RangeWarning {
  /** Stable per range and reason. Two ranges can share a code, so the sentence is not an id. */
  key: string;
  text: string;
  blocking: boolean;
}

/**
 * WHAT TO CALL A RANGE, and it is not always a register.
 *
 * Every range was called `Register ${emissionPointCode}` regardless of what it was, which was wrong in
 * two ways at once and wrong on the dashboard, where it is the first thing the owner reads:
 *
 *   * A CREDIT NOTE range has no register and never will — corrections are issued from this office, by
 *     hand, and the table on the invoicing screen already says "Chosen when you issue one". Sending
 *     the owner to look at a terminal over a credit note range is sending him to the wrong building.
 *   * An INVOICE range with no device attached is not a register either. It prints nothing at all until
 *     somebody attaches it, which is exactly what the table's "No register — prints nothing" says.
 *
 * The code is not unique across those, either: an invoice range and a credit note range may both be
 * `001`, so "Register 001" could name two different blocks of numbers in one banner with nothing to tell
 * them apart. The table on the invoicing screen has always drawn these distinctions carefully; this
 * is the same row described by the same site, so it says the same thing.
 */
export function nameFor(point: {
  emissionPointCode: string;
  documentType: EmissionPoint['documentType'];
  deviceId: string | null;
}): string {
  if (point.documentType === 'invoice') {
    return point.deviceId
      ? `Register ${point.emissionPointCode}`
      : `Invoice range ${point.emissionPointCode}`;
  }
  if (point.documentType === 'creditNote') return `Credit note range ${point.emissionPointCode}`;
  return `Debit note range ${point.emissionPointCode}`;
}

/** What the numbers in this range become. "invoice numbers" is not true of a credit note range. */
function nounFor(point: EmissionPoint, count: number): string {
  const one =
    point.documentType === 'invoice'
      ? 'invoice number'
      : point.documentType === 'creditNote'
        ? 'credit note number'
        : 'debit note number';
  return count === 1 ? one : `${one}s`;
}

function sentenceFor(point: EmissionPoint, reason: EmissionPoint['reasons'][number]): string | null {
  const name = nameFor(point);
  switch (reason) {
    case 'exhausted':
      return `${name} has used every number it has and cannot print any more. It needs a new CAI.`;
    case 'pastDeadline':
      return `${name} stopped printing on ${formatDate(point.deadline)} — its CAI expired. It needs a new one.`;
    case 'lowNumbers':
      return `${name} has only ${point.remaining.toLocaleString('en-US')} ${nounFor(point, point.remaining)} left.`;
    case 'closingDeadline':
      return `${name} stops printing after ${formatDate(point.deadline)} — ${countdown(point.daysUntilDeadline ?? 0)}.`;
    // A range the owner switched off on purpose is not news. It is reported on the table, where
    // its status already says so, and nowhere else.
    default:
      return null;
  }
}

export function warningsFor(point: EmissionPoint): RangeWarning[] {
  const blocking = point.severity === 'blocked';
  return point.reasons
    .map((reason) => ({ reason, text: sentenceFor(point, reason) }))
    .filter((w): w is { reason: EmissionPoint['reasons'][number]; text: string } => w.text !== null)
    .map((w) => ({ key: `${point.id}:${w.reason}`, text: w.text, blocking }));
}

/**
 * Running out of numbers or reaching the CAI's deadline stops invoicing outright, mid-service, with
 * no way to fix it that afternoon. The table alone buries that in two columns of digits, so the
 * count and the date are read here and said in a sentence.
 *
 * Shown on the dashboard as well as on the invoicing screen, because nobody opens the invoicing
 * screen on an ordinary Tuesday — and by the time you have a reason to open it, the range has
 * already run out and a replacement CAI is days away.
 */
export function RangeWarnings() {
  const query = useQuery({
    queryKey: listKey(EMISSION_POINTS_PATH),
    queryFn: () => rawApi<EmissionPoint[]>('GET', EMISSION_POINTS_PATH),
  });

  // Nothing at all rather than an error box when the read is refused: CAI ranges are owner-only,
  // and a manager on the dashboard can neither see this nor do anything about it. Failing quiet is
  // right here and only here — the banner is an extra, not the content of any screen.
  const warnings = (query.data ?? []).flatMap(warningsFor);
  if (warnings.length === 0) return null;

  const blocking = warnings.some((w) => w.blocking);
  return (
    <Alert
      color={blocking ? 'red' : 'yellow'}
      title={blocking ? 'A register has stopped printing invoices' : 'A CAI range is running out'}
      mb="md"
    >
      <Stack gap={4}>
        {warnings.map((w) => (
          <Text key={w.key} size="sm">
            {w.text}
          </Text>
        ))}
        <Text size="sm" mt={4}>
          Ask for the next CAI now, then add it on the Invoicing screen as a new number range.
        </Text>
      </Stack>
    </Alert>
  );
}
